import React, { memo, useMemo } from 'react';
import { TouchableOpacity, StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/text';
import { useCorkBoardColors } from './corkBoardStyles';
import { onColor } from '@/constants/shared/theme/onColor';

interface TagBadgeTag {
  name: string;
  color: string | null;
}

interface TagBadgeProps {
  tag: TagBadgeTag;
  size?: 'small' | 'medium';
  isActive?: boolean;
  onPress?: () => void;
}

export const TagBadge: React.FC<TagBadgeProps> = memo(({ tag, size = 'small', isActive = false, onPress }) => {
  const CB = useCorkBoardColors();
  const tagColor = tag.color || CB.defaultTagColor;

  const dynamicStyles = useMemo(() => ({
    container: {
      backgroundColor: isActive ? tagColor : hexToRgba(tagColor, 0.15),
      borderColor: isActive ? tagColor : 'transparent',
    },
    text: {
      // Active tags fill with tag.color, which comes from the API and can be
      // anything. The old hardcoded white measured 1.40:1 on a gold tag and
      // 2.15:1 on amber — unreadable, in both themes. Derive it from the fill.
      color: isActive ? onColor(tagColor) : tagColor,
    },
  }), [tagColor, isActive]);

  const isSmall = size === 'small';
  const containerStyle = [
    styles.container,
    isSmall ? styles.containerSmall : styles.containerMedium,
    dynamicStyles.container,
  ];
  const textStyle = [
    styles.text,
    isSmall ? styles.textSmall : styles.textMedium,
    dynamicStyles.text,
  ];

  const content = (
    <Text style={textStyle} numberOfLines={tag.name.includes(' ') ? 2 : 1}>{tag.name}</Text>
  );

  if (onPress) {
    return (
      <TouchableOpacity
        style={containerStyle}
        onPress={onPress}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={tag.name}
        accessibilityState={{ selected: isActive }}
      >
        {content}
      </TouchableOpacity>
    );
  }

  return (
    <View style={containerStyle} accessibilityLabel={tag.name}>
      {content}
    </View>
  );
});

TagBadge.displayName = 'TagBadge';

function hexToRgba(hex: string, alpha: number): string {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return `rgba(107, 114, 128, ${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 100,
    borderWidth: 1,
  },
  containerSmall: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    gap: 3,
  },
  containerMedium: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    gap: 5,
  },
  text: {
    fontWeight: '500',
  },
  textSmall: {
    fontSize: 11,
    lineHeight: 16,
  },
  textMedium: {
    fontSize: 13,
    lineHeight: 18,
  },
});
